// ══════════════════════════════════════════════════════════════════════
// Live Trade Chart (Sprint 3 Phase 5)
//
// Canvas-basierter Live-Chart für aktiv laufenden Trade.
// Style-Cousin vom TradeVisualizer.jsx (gleiche Drawing-Patterns), aber
// fokussiert auf das Live-Bild eines noch offenen Trades:
//   - Letzte ~80 M5-Bars als Candlesticks
//   - Entry-Linie + Entry-Marker (▼)
//   - Current SL (rot durchgezogen)
//   - Initial SL (rot gestrichelt, opacity 0.4) — nur wenn != current SL
//   - TP1-TP4 als horizontale grüne Linien
//       · durchgezogen wenn pending
//       · gestrichelt + opacity reduziert wenn done
//   - Runner-SL (lila durchgezogen) wenn runner_active
//   - SL-Zone (rot getönt zwischen entry und SL)
//   - TP-Zone (grün getönt zwischen entry und höchstem TP)
//   - Mouse-Crosshair mit Preis + Bar-Time
//
// Light-Mode only. Polled alle 5s über parent-Komponente.
// ══════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { fonts } from "../theme";
import { API_BASE } from "../config";

// ── LIGHT THEME ───────────────────────────────────────────────────────────
const C = {
  bg: "#f8fafc", panel: "#ffffff", header: "#f1f5f9",
  border: "rgba(0,0,0,0.08)", grid: "rgba(0,0,0,0.055)",
  text: "rgba(0,0,0,0.4)", textS: "rgba(0,0,0,0.88)", textF: "rgba(0,0,0,0.22)",
  bull: "#0d9488", bear: "#dc2626",
  bos: "#d97706", choch: "#7c3aed",   // amber for BoS, purple for CHoCH
  entry: "#16a34a", entryLong: "#16a34a", entryShort: "#dc2626",
  sl: "#dc2626", slDimmed: "rgba(220,38,38,0.4)",
  tp: "#0d9488", tpDone: "rgba(13,148,136,0.35)",
  runner: "#7c3aed",
  slFill: "rgba(220,38,38,0.06)",
  tpFill: "rgba(13,148,136,0.05)",
  xhair: "rgba(0,0,0,0.25)",
  axisBg: "#e2e8f0", axisText: "#0f172a",
};

const PAD = { T: 22, R: 80, B: 38, L: 8 };

// ── HELPERS ───────────────────────────────────────────────────────────────
const pMs = s => {
  if (!s) return null;
  const ms = Date.parse(s.includes("T") ? s : s.replace(" ", "T") + ":00Z");
  return isNaN(ms) ? null : ms;
};
const nS = (r, n = 7) => {
  const raw = r / n, e = Math.pow(10, Math.floor(Math.log10(raw)));
  return ([1, 2, 2.5, 5, 10].find(x => x * e >= raw) || 10) * e;
};
const near = (arr, ms) => {
  if (!arr?.length || !ms) return -1;
  let b = -1, bD = Infinity;
  arr.forEach((c, i) => { const d = Math.abs(c.t - ms); if (d < bD) { bD = d; b = i; } });
  return b;
};
function autoV(candles, et) {
  const len = candles?.length || 0;
  if (!len) return { start: 0, count: 1 };
  const eIdx = near(candles, pMs(et));
  const count = Math.min(len, 80);
  // Center on entry if it's in the buffer, else show most-recent
  if (eIdx >= 0) {
    return { start: Math.max(0, Math.min(len - count, Math.round(eIdx - count * 0.5))), count };
  }
  return { start: Math.max(0, len - count), count };
}
function clamp(v, len) {
  return {
    start: Math.max(0, Math.min(len - 1, v.start)),
    count: Math.max(15, Math.min(len, v.count)),
  };
}

// ── DRAW FUNCTIONS ────────────────────────────────────────────────────────
function drawChart(ctx, W, H, vis, eIdx, ep, sl, initSl, tps, tpDone, runnerActive, runnerSl, dir, signals) {
  const cW = W - PAD.L - PAD.R, cH = H - PAD.T - PAD.B;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
  if (!vis.length) return;

  // Compute range incl. all levels
  const lvls = [ep, sl, initSl, ...(tps || []), runnerActive ? runnerSl : null].filter(v => v && isFinite(v));
  let hi = Math.max(...vis.map(c => c.h), ...lvls);
  let lo = Math.min(...vis.map(c => c.l), ...lvls);
  const rng = hi - lo;
  hi += rng * 0.10; lo -= rng * 0.08;
  const tR = hi - lo;
  const bW = cW / vis.length;
  const cndW = Math.max(1, bW * 0.72);
  const toY = p => PAD.T + cH * (1 - (p - lo) / tR);
  const toX = i => PAD.L + (i + 0.5) * bW;

  // Border
  ctx.strokeStyle = C.border; ctx.lineWidth = 1;
  ctx.strokeRect(PAD.L, PAD.T, cW, cH);

  // Clip for chart-area-only drawing
  ctx.save();
  ctx.beginPath(); ctx.rect(PAD.L, PAD.T, cW, cH); ctx.clip();

  // Grid horizontal (price)
  const step = nS(tR);
  const sp = Math.ceil(lo / step) * step;
  ctx.strokeStyle = C.grid; ctx.lineWidth = 1;
  for (let p = sp; p <= hi + step * 0.01; p = Math.round((p + step) * 1e8) / 1e8) {
    ctx.beginPath(); ctx.moveTo(PAD.L, toY(p)); ctx.lineTo(PAD.L + cW, toY(p)); ctx.stroke();
  }

  // Grid vertical (time, every 55px ~)
  const vs = Math.max(1, Math.round(55 / bW));
  for (let i = 0; i < vis.length; i += vs) {
    ctx.beginPath(); ctx.moveTo(toX(i), PAD.T); ctx.lineTo(toX(i), PAD.T + cH); ctx.stroke();
  }

  // SL/entry zones (subtle background tint to show risk band)
  if (ep && sl) {
    const y1 = toY(Math.max(ep, sl)), y2 = toY(Math.min(ep, sl));
    ctx.fillStyle = C.slFill;
    ctx.fillRect(PAD.L, y1, cW, Math.max(1, y2 - y1));
  }
  if (ep && tps?.length) {
    const tpMax = dir === "long" ? Math.max(...tps) : Math.min(...tps);
    const y1 = toY(Math.max(ep, tpMax)), y2 = toY(Math.min(ep, tpMax));
    ctx.fillStyle = C.tpFill;
    ctx.fillRect(PAD.L, y1, cW, Math.max(1, y2 - y1));
  }

  // Helper to draw horizontal level lines
  const hL = (p, col, dashed, lw = 1, alpha = 1) => {
    if (!p || !isFinite(p)) return;
    const y = toY(p);
    if (y < PAD.T - 2 || y > PAD.T + cH + 2) return;
    ctx.save();
    ctx.strokeStyle = col; ctx.lineWidth = lw; ctx.globalAlpha = alpha;
    if (dashed) ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(PAD.L, y); ctx.lineTo(PAD.L + cW, y); ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  };

  // TP1-TP4 lines (drawn first so other lines layer on top)
  (tps || []).forEach((tp, i) => {
    if (!tp) return;
    const done = tpDone?.[i];
    // Skip TP3/TP4 visually as "irrelevant" if runner is active and they weren't hit
    const skippedForRunner = runnerActive && i >= 2 && !done;
    if (skippedForRunner) {
      hL(tp, C.tpDone, true, 1, 0.3);
    } else if (done) {
      hL(tp, C.tpDone, true, 1.5, 1);
    } else {
      hL(tp, C.tp, true, 1.2, 0.85);
    }
  });

  // Initial SL (only if it differs from current SL)
  if (initSl && Math.abs(initSl - sl) > 0.001) {
    hL(initSl, C.slDimmed, true, 1, 1);
  }

  // Current SL — solid, prominent
  hL(sl, C.sl, false, 2, 1);

  // Runner SL — purple, solid
  if (runnerActive && runnerSl) {
    hL(runnerSl, C.runner, false, 2, 1);
  }

  // Entry line — solid green/red based on direction
  hL(ep, dir === "long" ? C.entryLong : C.entryShort, false, 1.5, 1);

  // BoS / CHoCH structure-break overlay
  // Style: short horizontal dashed line + boxed type label centered on the line,
  // matching the TradeVisualizer convention. Lines drawn within the chart clip
  // so they don't bleed into axes.
  if (signals && signals.length) {
    ctx.save();
    ctx.beginPath(); ctx.rect(PAD.L, PAD.T, cW, cH); ctx.clip();
    signals.forEach((sig) => {
      const breakMs = pMs(sig.time);
      const startMs = sig.level_start ? pMs(sig.level_start) : null;
      const breakIdx = near(vis, breakMs);
      if (breakIdx < 0) return;
      const startIdx = startMs !== null ? Math.max(0, near(vis, startMs)) : Math.max(0, breakIdx - 20);
      const y = toY(sig.price);
      if (y < PAD.T - 2 || y > PAD.T + cH + 2) return;

      const col = sig.type === "CHoCH" ? C.choch : C.bos;
      const x1 = toX(startIdx);
      const x2 = toX(breakIdx);
      const midX = (x1 + x2) / 2;

      ctx.font = `bold 8.5px ${fonts.mono}`;
      const tw = ctx.measureText(sig.type).width + 10;
      const th = 14;
      const lx = midX - tw / 2;
      const ly = y - th / 2;

      // Dashed line in two segments leaving a gap for the label
      ctx.strokeStyle = col;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.7;
      ctx.setLineDash([5, 3]);
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(lx - 3, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(lx + tw + 3, y); ctx.lineTo(x2, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // Label box — clamp so it never leaves the chart area
      const clampedLx = Math.max(PAD.L + 2, Math.min(PAD.L + cW - tw - 2, lx));
      ctx.fillStyle = col;
      ctx.fillRect(clampedLx, ly, tw, th);
      ctx.fillStyle = "#fff";
      ctx.textAlign = "left";
      ctx.fillText(sig.type, clampedLx + 5, y + 3.5);
    });
    ctx.restore();
  }

  // Candles
  vis.forEach((c, i) => {
    const x = toX(i);
    const up = c.c >= c.o;
    const col = up ? C.bull : C.bear;
    ctx.strokeStyle = col; ctx.lineWidth = 1;
    // Wick
    ctx.beginPath(); ctx.moveTo(x, toY(c.h)); ctx.lineTo(x, toY(c.l)); ctx.stroke();
    // Body
    const bT = Math.min(toY(c.o), toY(c.c));
    const bH = Math.max(1, Math.abs(toY(c.c) - toY(c.o)));
    ctx.fillStyle = col;
    ctx.fillRect(x - cndW / 2, bT, cndW, bH);
  });

  // Entry vertical dash + arrow
  if (eIdx >= 0 && ep) {
    const x = toX(eIdx);
    // Vertical dashed line at entry bar
    ctx.strokeStyle = dir === "long" ? C.entryLong : C.entryShort;
    ctx.globalAlpha = 0.38; ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath(); ctx.moveTo(x, PAD.T); ctx.lineTo(x, PAD.T + cH); ctx.stroke();
    ctx.setLineDash([]); ctx.globalAlpha = 1;
    // Arrow
    const py = toY(ep);
    const lng = dir === "long";
    ctx.fillStyle = lng ? C.entryLong : C.entryShort;
    ctx.beginPath();
    if (lng) {
      ctx.moveTo(x, py + 8);
      ctx.lineTo(x - 9, py + 24);
      ctx.lineTo(x + 9, py + 24);
    } else {
      ctx.moveTo(x, py - 8);
      ctx.lineTo(x - 9, py - 24);
      ctx.lineTo(x + 9, py - 24);
    }
    ctx.closePath(); ctx.fill();
    ctx.font = `bold 9px ${fonts.mono}`;
    ctx.fillStyle = "#fff"; ctx.textAlign = "center";
    ctx.fillText("E", x, lng ? py + 20 : py - 16);
  }

  ctx.restore();

  // ── Axes (outside clip) ──────────────────────────────────────────────
  ctx.font = `10.5px ${fonts.mono}`;
  ctx.textAlign = "left";
  ctx.fillStyle = C.text;
  for (let p = sp; p <= hi + step * 0.01; p = Math.round((p + step) * 1e8) / 1e8) {
    const y = toY(p);
    if (y < PAD.T - 4 || y > PAD.T + cH + 4) continue;
    ctx.fillText(p.toFixed(2), PAD.L + cW + 6, y + 3.5);
  }

  // Right-axis labels for important levels
  const aL = (p, col, lbl) => {
    if (!p || !isFinite(p)) return;
    const y = toY(p);
    if (y < PAD.T - 4 || y > PAD.T + cH + 4) return;
    ctx.fillStyle = col;
    ctx.fillRect(PAD.L + cW + 1, y - 9, PAD.R - 3, 18);
    ctx.fillStyle = "#fff";
    ctx.font = `bold 9px ${fonts.mono}`;
    ctx.textAlign = "left";
    ctx.fillText(`${lbl} ${p.toFixed(2)}`, PAD.L + cW + 4, y + 4);
  };
  aL(ep, dir === "long" ? C.entryLong : C.entryShort, "E");
  aL(sl, C.sl, "SL");
  if (runnerActive && runnerSl) aL(runnerSl, C.runner, "R");
  (tps || []).forEach((tp, i) => {
    if (!tp) return;
    const done = tpDone?.[i];
    if (done) return;  // skip done TPs in axis labels (less clutter)
    aL(tp, C.tp, `T${i + 1}`);
  });

  // Time axis (bottom)
  ctx.fillStyle = C.text;
  ctx.font = `9.5px ${fonts.mono}`;
  ctx.textAlign = "center";
  const ls = Math.max(1, Math.ceil(vis.length / Math.max(1, Math.floor(cW / 100))));
  vis.forEach((c, i) => {
    if (i % ls !== 0) return;
    const d = new Date(c.t);
    const lbl = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    ctx.fillText(lbl, toX(i), H - 6);
  });
}

function drawXhair(ctx, W, H, vis, hov) {
  if (!hov || !vis.length) return;
  const cW = W - PAD.L - PAD.R, cH = H - PAD.T - PAD.B;
  if (hov.x < PAD.L || hov.x > PAD.L + cW) return;
  let hi = Math.max(...vis.map(c => c.h));
  let lo = Math.min(...vis.map(c => c.l));
  const rng = hi - lo;
  hi += rng * 0.10; lo -= rng * 0.08;
  const tR = hi - lo;
  const bW = cW / vis.length;
  const toY = p => PAD.T + cH * (1 - (p - lo) / tR);
  const toX = i => PAD.L + (i + 0.5) * bW;

  ctx.strokeStyle = C.xhair; ctx.lineWidth = 1;
  ctx.setLineDash([3, 4]);
  ctx.beginPath(); ctx.moveTo(hov.x, PAD.T); ctx.lineTo(hov.x, PAD.T + cH); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(PAD.L, hov.y); ctx.lineTo(PAD.L + cW, hov.y); ctx.stroke();
  ctx.setLineDash([]);

  // Price label on right axis
  if (hov.y > PAD.T && hov.y < PAD.T + cH) {
    const p = lo + (1 - (hov.y - PAD.T) / cH) * tR;
    ctx.fillStyle = C.axisBg;
    ctx.fillRect(PAD.L + cW, hov.y - 10, PAD.R, 20);
    ctx.strokeStyle = C.xhair; ctx.lineWidth = 1;
    ctx.strokeRect(PAD.L + cW, hov.y - 10, PAD.R, 20);
    ctx.fillStyle = C.axisText;
    ctx.font = `bold 10px ${fonts.mono}`;
    ctx.textAlign = "left";
    ctx.fillText(p.toFixed(2), PAD.L + cW + 5, hov.y + 3.5);
  }

  // Time label on bottom
  if (hov.idx != null && vis[hov.idx]) {
    const x = toX(hov.idx);
    const d = new Date(vis[hov.idx].t);
    const lbl = `${d.toLocaleDateString("de-DE", { month: "2-digit", day: "2-digit" })} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    ctx.font = `9.5px ${fonts.mono}`;
    ctx.textAlign = "center";
    const tw = ctx.measureText(lbl).width + 14;
    ctx.fillStyle = C.axisBg;
    ctx.fillRect(x - tw / 2, H - PAD.B + 3, tw, 17);
    ctx.strokeStyle = C.xhair; ctx.lineWidth = 1;
    ctx.strokeRect(x - tw / 2, H - PAD.B + 3, tw, 17);
    ctx.fillStyle = C.axisText;
    ctx.fillText(lbl, x, H - PAD.B + 14);
  }
}

// ── CANVAS CHART (RAF-based, anti-flicker pattern from TradeVisualizer) ─
function CandleChart({ candles, entryTime, entryPrice, slPrice, initialSl, tpLevels, tpDone, runnerActive, runnerSl, direction, signals }) {
  const canvasRef = useRef(null);
  const backingRef = useRef(null);
  const containerRef = useRef(null);
  const ohlcRef = useRef(null);
  const [dim, setDim] = useState({ w: 1100, h: 400 });

  // Mutable render state — RAF reads this, no React re-renders triggered
  const R = useRef({
    dim: { w: 1100, h: 400 },
    candles: [], entryTime: null, ep: 0, sl: null, initSl: null,
    tps: [], tpDone: [], runnerActive: false, runnerSl: 0, dir: "long",
    signals: [],
    view: null, hover: null, dirty: true,
  });

  // Sync props → R (no array re-creation on each render to avoid spurious firing)
  useEffect(() => {
    Object.assign(R.current, {
      candles: candles || [],
      entryTime,
      ep: entryPrice,
      sl: slPrice,
      initSl: initialSl,
      tps: tpLevels || [],
      tpDone: tpDone || [],
      runnerActive: !!runnerActive,
      runnerSl: runnerSl || 0,
      dir: direction || "long",
      signals: signals || [],
      dirty: true,
    });
    // Reset view so it re-centers on entry when data changes shape
    R.current.view = null;
  }, [candles, entryTime, entryPrice, slPrice, initialSl, tpLevels, tpDone, runnerActive, runnerSl, direction, signals]);

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([e]) => {
      const { width: w, height: h } = e.contentRect;
      if (w > 20 && h > 20) setDim({ w: Math.floor(w), h: Math.floor(h) });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Apply dim to canvas + mark dirty
  useEffect(() => {
    if (!canvasRef.current) return;
    canvasRef.current.width = dim.w;
    canvasRef.current.height = dim.h;
    R.current.dim = dim;
    R.current.dirty = true;
  }, [dim]);

  // RAF render loop — empty deps, never restarts
  useEffect(() => {
    backingRef.current = document.createElement("canvas");
    let alive = true;
    const raf = { id: null };

    function frame() {
      if (!alive) return;
      const canvas = canvasRef.current, backing = backingRef.current;
      if (!canvas || !backing) { raf.id = requestAnimationFrame(frame); return; }

      const { dim: d, candles: cs, entryTime: et, ep, sl, initSl, tps, tpDone, runnerActive, runnerSl, dir, signals, hover, dirty } = R.current;
      const len = cs.length;
      const auto = autoV(cs, et);
      const view = R.current.view ? clamp(R.current.view, len) : clamp(auto, len);
      const vis = cs.slice(view.start, view.start + view.count);
      const eIdx = near(vis, pMs(et));
      const ctx = canvas.getContext("2d");

      if (dirty) {
        if (backing.width !== d.w || backing.height !== d.h) {
          backing.width = d.w; backing.height = d.h;
        }
        drawChart(backing.getContext("2d"), d.w, d.h, vis, eIdx, ep, sl, initSl, tps, tpDone, runnerActive, runnerSl, dir, signals);
        R.current.dirty = false;
      }

      ctx.clearRect(0, 0, d.w, d.h);
      ctx.drawImage(backing, 0, 0);
      if (hover) drawXhair(ctx, d.w, d.h, vis, hover);

      raf.id = requestAnimationFrame(frame);
    }
    raf.id = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf.id); };
  }, []);

  const onMove = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const { dim: d, candles: cs, entryTime: et } = R.current;
    const x = (e.clientX - rect.left) * (d.w / rect.width);
    const y = (e.clientY - rect.top) * (d.h / rect.height);
    const view = R.current.view ? clamp(R.current.view, cs.length) : clamp(autoV(cs, et), cs.length);
    const cW = d.w - PAD.L - PAD.R;
    const bW = cW / view.count;
    const idx = Math.max(0, Math.min(view.count - 1, Math.floor((x - PAD.L) / bW)));
    R.current.hover = { x, y, idx };

    // OHLC display
    const vis = cs.slice(view.start, view.start + view.count);
    const c = vis[idx];
    if (ohlcRef.current && c) {
      const up = c.c >= c.o;
      ohlcRef.current.innerHTML =
        `<span style="opacity:.4">O</span>&nbsp;<b>${c.o.toFixed(2)}</b>&ensp;` +
        `<span style="opacity:.4">H</span>&nbsp;<b style="color:${C.bull}">${c.h.toFixed(2)}</b>&ensp;` +
        `<span style="opacity:.4">L</span>&nbsp;<b style="color:${C.bear}">${c.l.toFixed(2)}</b>&ensp;` +
        `<span style="opacity:.4">C</span>&nbsp;<b style="color:${up ? C.bull : C.bear}">${c.c.toFixed(2)}</b>`;
    }
  }, []);

  const onLeave = useCallback(() => {
    R.current.hover = null;
    if (ohlcRef.current) ohlcRef.current.innerHTML = "";
  }, []);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden", userSelect: "none" }}>
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "crosshair" }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      />
      <div ref={ohlcRef} style={{
        position: "absolute", top: 6, left: 13,
        fontSize: 11, fontFamily: fonts.mono,
        color: "rgba(0,0,0,0.6)", pointerEvents: "none", userSelect: "none",
        letterSpacing: "-0.3px",
      }} />
    </div>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────
export default function LiveTradeChart({ strategyKey, tradeState }) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable key derived from current trade-state — re-fetches only when meaningful change
  const fetchKey = useMemo(() => {
    if (!tradeState || !tradeState.in_trade) return null;
    return [
      strategyKey,
      tradeState.entry_time,
      tradeState.bars_in_trade,
      tradeState.sl_price,
      JSON.stringify(tradeState.tp_done || []),
      tradeState.runner_active ? "R" : "",
    ].join("|");
  }, [strategyKey, tradeState]);

  useEffect(() => {
    if (!fetchKey) return;
    let alive = true;
    setLoading(true);
    setError(null);

    const url = `${API_BASE}/runners/${encodeURIComponent(strategyKey)}/chart-data?n=80`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (!alive) return;
        setChartData(data);
        setLoading(false);
      })
      .catch(e => {
        if (!alive) return;
        setError(e.message);
        setLoading(false);
      });

    return () => { alive = false; };
  }, [fetchKey]);

  if (!tradeState || !tradeState.in_trade) return null;

  const bars = chartData?.bars || [];

  return (
    <div style={{
      marginTop: 14,
      background: C.bg,
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 14px", background: C.header, borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: "0.8px",
          textTransform: "uppercase", color: C.textF, fontFamily: fonts.mono,
        }}>
          Live Chart — M5
        </span>
        <span style={{
          fontSize: 9, color: C.text, fontFamily: fonts.mono,
        }}>
          {bars.length > 0 ? `${bars.length} bars` : ""}
        </span>
      </div>

      {/* Canvas area */}
      <div style={{ height: 400, position: "relative" }}>
        {loading && bars.length === 0 && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, color: C.text, fontFamily: fonts.mono,
          }}>
            loading chart…
          </div>
        )}
        {error && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, color: C.bear, fontFamily: fonts.mono,
          }}>
            chart error: {error}
          </div>
        )}
        {bars.length > 0 && (
          <CandleChart
            candles={bars}
            entryTime={tradeState.entry_time}
            entryPrice={tradeState.entry_price}
            slPrice={tradeState.sl_price}
            initialSl={tradeState.initial_sl}
            tpLevels={tradeState.tp_levels}
            tpDone={tradeState.tp_done}
            runnerActive={tradeState.runner_active}
            runnerSl={tradeState.runner_sl}
            direction={tradeState.direction}
            signals={chartData?.signals || []}
          />
        )}
      </div>

      {/* Legend */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "8px 16px",
        padding: "6px 14px", background: "#fff",
        borderTop: `1px solid ${C.border}`,
        fontSize: 9, fontFamily: fonts.mono, color: C.text,
      }}>
        <LegendItem color={C.entryLong} label="Entry" />
        <LegendItem color={C.sl} label="Current SL" />
        {tradeState.sl_price !== tradeState.initial_sl && (
          <LegendItem color={C.slDimmed} label="Initial SL" dashed />
        )}
        <LegendItem color={C.tp} label="TP pending" dashed />
        <LegendItem color={C.tpDone} label="TP done" dashed />
        {tradeState.runner_active && (
          <LegendItem color={C.runner} label="Runner SL" />
        )}
        <LegendItem color={C.bos} label="BoS" dashed />
        <LegendItem color={C.choch} label="CHoCH" dashed />
      </div>
    </div>
  );
}

function LegendItem({ color, label, dashed }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{
        display: "inline-block",
        width: 12, height: 2,
        background: dashed ? "transparent" : color,
        borderTop: dashed ? `2px dashed ${color}` : "none",
      }} />
      {label}
    </span>
  );
}
